import type { Orderbook } from "./api";

// Client-side order preview, computed from the order book we already poll.
//
// Deliberately NOT a call to Daya's /orders/quote: that endpoint spends the authenticated
// 100 req/min budget, and a preview updates on every keystroke. Walking the local book is
// instant, free, and accurate enough to show the user what they'll get. The authoritative
// number still comes from Daya at execution time; this is guidance, and is labelled as such.
//
// Market semantics: `quantity` is in the BASE asset (USD), matching Daya. A market BUY walks
// the asks (you take the best offers); a market SELL walks the bids.

export type Side = "buy" | "sell";
export type OrderType = "market" | "limit";

export interface QuotePreview {
  /** USD amount being traded (base). */
  quantity: number;
  /** Weighted-average price across the levels consumed (NGN per USD). */
  avgPrice: number;
  /** Total NGN in/out (quote asset). */
  total: number;
  /** True if the book didn't have enough depth to fill the whole quantity. */
  insufficientLiquidity: boolean;
  /** Fraction of `quantity` that the book could actually fill (0–1). */
  fillableFraction: number;
}

const num = (s: string) => Number(s);

/**
 * Walk one side of the book, consuming `quantity` USD of depth, returning the volume-weighted
 * average price and total. Levels must be sorted best-first (asks ascending, bids descending).
 */
const walk = (
  levels: { price: string; quantity: string }[],
  quantity: number
): QuotePreview => {
  let remaining = quantity;
  let filled = 0;
  let totalNgn = 0;

  for (const level of levels) {
    if (remaining <= 0) break;
    const levelQty = num(level.quantity);
    const price = num(level.price);
    const take = Math.min(remaining, levelQty);
    totalNgn += take * price;
    filled += take;
    remaining -= take;
  }

  const avgPrice = filled > 0 ? totalNgn / filled : 0;
  return {
    quantity,
    avgPrice,
    total: totalNgn,
    insufficientLiquidity: remaining > 1e-8,
    fillableFraction: quantity > 0 ? filled / quantity : 0,
  };
};

export const previewMarket = (
  book: Orderbook | undefined,
  side: Side,
  quantity: number
): QuotePreview | null => {
  if (!book || !Number.isFinite(quantity) || quantity <= 0) return null;
  const levels = side === "buy" ? book.asks : book.bids;
  if (!Array.isArray(levels) || levels.length === 0) return null;
  return walk(levels, quantity);
};

/** Limit orders fill at (at best) the limit price, total is a simple product. */
export const previewLimit = (
  side: Side,
  quantity: number,
  price: number
): QuotePreview | null => {
  if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(price) || price <= 0) {
    return null;
  }
  return {
    quantity,
    avgPrice: price,
    total: quantity * price,
    insufficientLiquidity: false,
    fillableFraction: 1,
  };
};
